import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CreditCard } from "lucide-react";
import axios from "axios";
import {
  formatPlanPrice,
  getPickerPlansForInterval,
} from "../../utils/checkoutPlans";

function readOrgId() {
  try {
    return JSON.parse(localStorage.getItem("organization"))?.orgId?.S ?? null;
  } catch {
    return null;
  }
}

/**
 * Monthly/annual plan list that starts Stripe Checkout for the current org.
 * Errors are reported through onError so the host page can place them.
 */
export default function CheckoutPlanPicker({ onError }) {
  const [billingInterval, setBillingInterval] = useState("year");
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [checkoutLoader, setCheckoutLoader] = useState(false);

  const plans = useMemo(
    () => getPickerPlansForInterval(billingInterval),
    [billingInterval]
  );

  useEffect(() => {
    if (plans.length === 0) {
      setSelectedPriceId(null);
      return;
    }
    const stillValid = plans.some((p) => p.priceId === selectedPriceId);
    if (!stillValid) {
      const team = plans.find((p) => p.planName === "Team");
      setSelectedPriceId((team || plans[0]).priceId);
    }
  }, [plans, selectedPriceId]);

  const selectedPlan = plans.find((p) => p.priceId === selectedPriceId);

  const reportError = (msg) => {
    if (onError) onError(msg);
  };

  const openCheckout = async () => {
    reportError(null);
    if (!selectedPriceId) {
      reportError("Please select a plan.");
      return;
    }
    setCheckoutLoader(true);
    try {
      const orgId = readOrgId();
      if (!orgId) {
        reportError("Unable to start checkout. Please contact support.");
        return;
      }
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "stripe/create-checkout-session",
        {
          organization_id: orgId,
          price_id: selectedPriceId,
        }
      );
      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        reportError("Unable to start checkout. Please contact support.");
      }
    } catch (error) {
      console.error(error);
      reportError(
        error.response?.data?.message ||
          "Unable to start checkout. Please contact support."
      );
    } finally {
      setCheckoutLoader(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          p: 0.5,
          mb: 2,
          borderRadius: "10px",
          bgcolor: "#F1F5F9",
        }}
      >
        {[
          { value: "month", label: "Monthly" },
          { value: "year", label: "Annual" },
        ].map((opt) => (
          <Button
            key={opt.value}
            fullWidth
            disableElevation
            onClick={() => setBillingInterval(opt.value)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              py: 0.9,
              borderRadius: "8px",
              bgcolor: billingInterval === opt.value ? "#fff" : "transparent",
              color: billingInterval === opt.value ? "#111827" : "#64748B",
              boxShadow:
                billingInterval === opt.value
                  ? "0 1px 3px rgba(0,0,0,0.08)"
                  : "none",
              "&:hover": {
                bgcolor: billingInterval === opt.value ? "#fff" : "#E2E8F0",
              },
            }}
          >
            {opt.label}
          </Button>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
        {plans.map((plan) => {
          const selected = plan.priceId === selectedPriceId;
          const priceLabel = formatPlanPrice(plan.unitAmount, plan.currency);
          const period = plan.interval === "year" ? "/year" : "/month";
          return (
            <Box
              key={plan.priceId}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedPriceId(plan.priceId)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedPriceId(plan.priceId);
                }
              }}
              sx={{
                textAlign: "left",
                border: selected ? "2px solid #7C3AED" : "1px solid #E5E7EB",
                borderRadius: "10px",
                p: 2,
                cursor: "pointer",
                bgcolor: selected ? "#F5F3FF" : "#FFFFFF",
                transition: "border-color 0.15s, background-color 0.15s",
                "&:hover": {
                  borderColor: selected ? "#7C3AED" : "#C4B5FD",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  mb: 0.5,
                }}
              >
                <Typography fontWeight={600} fontSize={15}>
                  {plan.planName}
                </Typography>
                <Typography fontWeight={600} fontSize={15}>
                  {priceLabel}
                  <Typography
                    component="span"
                    fontSize={12}
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {period}
                  </Typography>
                </Typography>
              </Box>
              <Typography fontSize={12} color="text.secondary">
                {plan.monthlyCalls} calls/mo · {plan.team_members}{" "}
                {plan.team_members === 1 ? "member" : "members"}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Typography fontSize={13} color="text.secondary" sx={{ mb: 2 }}>
        <Box
          component="a"
          href="https://www.twilanswer.com/pricing"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "#7C3AED",
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          See full plan details
        </Box>{" "}
        on our pricing page.
      </Typography>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        startIcon={
          checkoutLoader ? (
            <CircularProgress size={16} sx={{ color: "white" }} />
          ) : (
            <CreditCard size={16} />
          )
        }
        disabled={checkoutLoader || !selectedPlan}
        sx={{
          py: 1.2,
          borderRadius: "8px",
          mb: 1.5,
          textTransform: "none",
          fontWeight: 600,
        }}
        onClick={openCheckout}
      >
        {selectedPlan ? `Subscribe to ${selectedPlan.planName}` : "Subscribe"}
      </Button>
    </>
  );
}
